import { HttpClient, httpResource } from '@angular/common/http';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import {
  DmAvatar,
  DmButton,
  DmIcon,
  DmInput,
  DmLabel,
  DmModal,
  DmModalFooter,
  DmModalHeader,
} from '@digitale-menschen/component-library-angular';
import { API_CONFIGURATION } from '@lexplus-environments';
import {
  AvatarFromPersonDirective,
  AvatarPerson,
  fieldErrorMessage,
  notify,
  notifyRequestFailure,
  OverlayDismissDirective,
} from '@lexplus-shared-lib';
import {
  getLexplusCreateKontaktMessageDtoInitialValue,
  kontaktControllerCreateMessageV1,
  kundeProjektleiterControllerGetProjektleiterV1,
  LexplusCreateKontaktMessageDto,
  lexplusCreateKontaktMessageDtoSchema,
  LexplusKundeProjektleiterDto,
} from '@lexplus-user-api';
import { lastValueFrom } from 'rxjs';

/**
 * The support contact dialog opened from the sidebar.
 *
 * The contact person is the Kunde's Projektleiter, read each time the dialog opens. There is
 * message is posted to the Kontakt endpoint, which mails it to that person.
 */
@Component({
  selector: 'lp-kontakt-modal',
  imports: [
    AvatarFromPersonDirective,
    DmModal,
    DmModalHeader,
    DmModalFooter,
    DmButton,
    DmInput,
    DmIcon,
    DmAvatar,
    DmLabel,
    FormRoot,
    FormField,
    OverlayDismissDirective,
  ],
  templateUrl: './kontakt-modal.component.html',
  styleUrl: './kontakt-modal.component.scss',
  host: { class: 'lp-kontakt-modal' },
})
export class KontaktModalComponent {
  public readonly isModalOpen = input<boolean>(false);

  public readonly closeClick = output<void>();

  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  protected readonly isSending = signal<boolean>(false);
  protected readonly messageModel = signal<LexplusCreateKontaktMessageDto>(getLexplusCreateKontaktMessageDtoInitialValue());
  protected readonly messageForm = form(this.messageModel, lexplusCreateKontaktMessageDtoSchema, {
    submission: { action: () => this.send() },
  });
  protected readonly canSend = computed<boolean>(() => this.messageForm().valid() && !this.isSending());

  private readonly projektleiterResource = httpResource<LexplusKundeProjektleiterDto>(() =>
    this.isModalOpen() ? `${this.apiConfiguration.baseUrlUser}${kundeProjektleiterControllerGetProjektleiterV1.PATH}` : undefined,
  );

  protected readonly projektleiter = computed<LexplusKundeProjektleiterDto | null>(() =>
    this.projektleiterResource.hasValue() ? this.projektleiterResource.value() : null,
  );

  protected readonly contactName = computed<string>(() => {
    const projektleiter = this.projektleiter();
    return [projektleiter?.vorname, projektleiter?.nachname].filter(Boolean).join(' ');
  });

  /** The Projektleitung is always a Neosys person. */
  protected readonly contactPerson = computed<AvatarPerson | null>(() => {
    const projektleiter = this.projektleiter();
    return projektleiter ? { ...projektleiter, isNeosys: true } : null;
  });

  private readonly profilbildResource = httpResource.blob(() => {
    const profilbildUrl = this.projektleiter()?.profilbildUrl;
    return profilbildUrl ? `${this.apiConfiguration.baseUrlUser}${profilbildUrl}` : undefined;
  });

  protected readonly contactImageUrl = signal<string | null>(null);

  private readonly contactImageUrlEffect = effect((onCleanup) => {
    if (!this.profilbildResource.hasValue()) {
      this.contactImageUrl.set(null);
      return;
    }
    const objectUrl = URL.createObjectURL(this.profilbildResource.value());
    this.contactImageUrl.set(objectUrl);
    onCleanup(() => URL.revokeObjectURL(objectUrl));
  });

  private readonly clearMessageWhenClosedEffect = effect(() => {
    if (!this.isModalOpen()) {
      this.messageForm().reset(getLexplusCreateKontaktMessageDtoInitialValue());
    }
  });

  protected readonly title = $localize`:@@kontakt.modal.titel:Kontakt`;
  protected readonly description = $localize`:@@kontakt.modal.beschreibung:Brauchen Sie Hilfe?`;
  protected readonly contactLabel = $localize`:@@kontakt.modal.ansprechpartner:Ihre Ansprechperson bei Neosys`;
  protected readonly projektleitungLabel = $localize`:@@kontakt.modal.projektleitung:Projektleitung`;
  private readonly requiredMessage = $localize`:@@kontakt.modal.nachrichtErforderlich:Bitte geben Sie eine Nachricht ein.`;
  protected readonly messageLabel = computed<string>(() => {
    const name = this.contactName();
    return name
      ? $localize`:@@kontakt.modal.nachrichtLabel:Ihre Nachricht an ${name}:INTERPOLATION:`
      : $localize`:@@kontakt.modal.nachrichtLabelAllgemein:Ihre Nachricht`;
  });
  protected readonly messagePlaceholder = $localize`:@@kontakt.modal.nachrichtPlatzhalter:Beschreiben Sie Ihre Fragen, Anliegen etc.`;
  protected readonly responseHint = $localize`:@@kontakt.modal.antwortHinweis:Wir melden uns so schnell wie möglich bei Ihnen.`;
  protected readonly messageErrorMessage = computed(() => fieldErrorMessage(this.messageForm.message(), this.requiredMessage));
  protected readonly closeLabel = $localize`:@@common.schliessen:Schliessen`;
  protected readonly sendLabel = $localize`:@@kontakt.modal.senden:Nachricht senden`;

  protected close(): void {
    this.closeClick.emit();
  }

  protected async send(): Promise<void> {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    try {
      const response = await lastValueFrom(
        kontaktControllerCreateMessageV1(this.httpClient, this.apiConfiguration.baseUrlUser, { body: this.messageForm().value() }),
      );
      if (!response.body.success) {
        throw new Error();
      }
      notify.success($localize`:@@kontakt.modal.gesendet:Nachricht gesendet`);
      this.closeClick.emit();
    } catch (error) {
      notifyRequestFailure(error);
    } finally {
      this.isSending.set(false);
    }
  }
}
